<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
  voiceAgent: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit', 'cancel']);
const { t } = useI18n();

const languageOptions = [
  { value: 'multilingual', label: 'Multilingual' },
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'es-ES', label: 'Spanish (Spain)' },
  { value: 'es-MX', label: 'Spanish (Mexico)' },
  { value: 'fr-FR', label: 'French' },
  { value: 'de-DE', label: 'German' },
  { value: 'it-IT', label: 'Italian' },
  { value: 'pt-BR', label: 'Portuguese (Brazil)' },
  { value: 'pt-PT', label: 'Portuguese (Portugal)' },
  { value: 'nl-NL', label: 'Dutch' },
  { value: 'pl-PL', label: 'Polish' },
  { value: 'ru-RU', label: 'Russian' },
  { value: 'ja-JP', label: 'Japanese' },
  { value: 'ko-KR', label: 'Korean' },
  { value: 'zh-CN', label: 'Chinese (Simplified)' },
  { value: 'zh-TW', label: 'Chinese (Traditional)' },
  { value: 'hi-IN', label: 'Hindi' },
  { value: 'ar-SA', label: 'Arabic' },
];

// Voice options by provider
const voiceOptionsByProvider = {
  openai: [
    { value: 'alloy', label: 'Alloy' },
    { value: 'echo', label: 'Echo' },
    { value: 'fable', label: 'Fable' },
    { value: 'onyx', label: 'Onyx' },
    { value: 'nova', label: 'Nova' },
    { value: 'shimmer', label: 'Shimmer' },
  ],
  cartesia: [
    {
      value: '71a7ad14-091c-4e8e-a314-022ece01c121',
      label: 'British Reading Lady',
    },
    { value: '79a125e8-cd45-4c13-8a67-188112f4dd22', label: 'Barbershop Man' },
    {
      value: 'a0e99841-438c-4a64-b679-ae501e7d6091',
      label: 'Classy British Man',
    },
    {
      value: '421b3369-f63f-4b03-8980-37a44df1d4e8',
      label: 'Storyteller Lady',
    },
    { value: '820a3788-2b37-4d21-847a-b65d8a68c99a', label: 'Indian Lady' },
    { value: 'ee9959d8-7f6f-40b3-9f92-5a0d6e882a25', label: 'Anime Girl' },
    { value: 'bf991597-6c13-47e4-8411-91ec2de5c466', label: 'Australian Male' },
    { value: '5619d38c-cf51-4d8e-9575-48f61a280413', label: 'Meditation Lady' },
  ],
  elevenlabs: [
    { value: 'EXAVITQu4vr4xnSDxMaL', label: 'Sarah' },
    { value: 'pNInz6obpgDQGcFmaJgB', label: 'Adam' },
    { value: 'ErXwobaYiN019PkySvjV', label: 'Antoni' },
    { value: 'VR6AewLTigWG4xSOukaG', label: 'Arnold' },
    { value: 'MF3mGyEYCl7XYWbV9V6O', label: 'Bella' },
    { value: 'TxGEqnHWrfWFTfGW9XjX', label: 'Josh' },
  ],
  deepgram: [
    { value: 'aura-asteria-en', label: 'Asteria' },
    { value: 'aura-luna-en', label: 'Luna' },
    { value: 'aura-stella-en', label: 'Stella' },
    { value: 'aura-athena-en', label: 'Athena' },
    { value: 'aura-hera-en', label: 'Hera' },
    { value: 'aura-orion-en', label: 'Orion' },
    { value: 'aura-arcas-en', label: 'Arcas' },
    { value: 'aura-perseus-en', label: 'Perseus' },
    { value: 'aura-angus-en', label: 'Angus' },
    { value: 'aura-orpheus-en', label: 'Orpheus' },
    { value: 'aura-helios-en', label: 'Helios' },
    { value: 'aura-zeus-en', label: 'Zeus' },
  ],
};

const voiceProviderOptions = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'elevenlabs', label: 'ElevenLabs' },
  { value: 'cartesia', label: 'Cartesia' },
  { value: 'deepgram', label: 'Deepgram' },
];

// Default voice IDs by provider
const defaultVoiceByProvider = {
  openai: 'alloy',
  cartesia: '71a7ad14-091c-4e8e-a314-022ece01c121', // British Reading Lady
  elevenlabs: 'EXAVITQu4vr4xnSDxMaL', // Sarah
  deepgram: 'aura-asteria-en', // Asteria
};

const formData = ref({
  name: '',
  description: '',
  voice_id: defaultVoiceByProvider.cartesia,
  language: 'multilingual',
  voice_provider: 'cartesia',
  voice_speed: 1.0,
  voice_temperature: 0.7,
  initial_message: '',
  active: true,
});

const isSubmitting = ref(false);

// Get voice options for current provider
const currentVoiceOptions = computed(() => {
  return (
    voiceOptionsByProvider[formData.value.voice_provider] ||
    voiceOptionsByProvider.cartesia
  );
});

// Watch for provider changes and update voice_id to default for new provider
watch(
  () => formData.value.voice_provider,
  (newProvider, oldProvider) => {
    // Only auto-change voice if provider actually changed (not initial load)
    if (oldProvider && newProvider !== oldProvider) {
      formData.value.voice_id =
        defaultVoiceByProvider[newProvider] || defaultVoiceByProvider.cartesia;
    }
  }
);

watch(
  () => props.voiceAgent,
  newAgent => {
    if (newAgent && Object.keys(newAgent).length > 0) {
      formData.value = {
        name: newAgent.name || '',
        description: newAgent.description || '',
        voice_id: newAgent.voice_id || defaultVoiceByProvider.cartesia,
        language: newAgent.language || 'en-US',
        voice_provider: newAgent.voice_provider || 'cartesia',
        voice_speed: newAgent.voice_speed || 1.0,
        voice_temperature: newAgent.voice_temperature || 0.7,
        initial_message: newAgent.initial_message || '',
        active: newAgent.active ?? true,
      };
    }
  },
  { immediate: true }
);

const handleSubmit = async () => {
  if (!formData.value.name) {
    return;
  }

  isSubmitting.value = true;
  try {
    emit('submit', formData.value);
  } finally {
    isSubmitting.value = false;
  }
};

const handleCancel = () => {
  emit('cancel');
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="formData.name"
      :label="t('AI_VOICE.VOICE_AGENTS.FORM.NAME')"
      :placeholder="t('AI_VOICE.VOICE_AGENTS.FORM.NAME_PLACEHOLDER')"
      required
    />

    <TextArea
      v-model="formData.description"
      :label="t('AI_VOICE.VOICE_AGENTS.FORM.DESCRIPTION')"
      :placeholder="t('AI_VOICE.VOICE_AGENTS.FORM.DESCRIPTION_PLACEHOLDER')"
      rows="3"
    />

    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('AI_VOICE.VOICE_AGENTS.FORM.LANGUAGE') }}
      </label>
      <select
        v-model="formData.language"
        class="px-3 py-2 text-sm border rounded-lg bg-n-solid-1 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <option
          v-for="option in languageOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
    </div>

    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-n-slate-12">
        Voice Provider
      </label>
      <select
        v-model="formData.voice_provider"
        class="px-3 py-2 text-sm border rounded-lg bg-n-solid-1 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <option
          v-for="option in voiceProviderOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
    </div>

    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('AI_VOICE.VOICE_AGENTS.FORM.VOICE') }}
      </label>
      <select
        v-model="formData.voice_id"
        class="px-3 py-2 text-sm border rounded-lg bg-n-solid-1 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <option
          v-for="option in currentVoiceOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
    </div>

    <div class="flex gap-4">
      <div class="flex flex-col gap-1 flex-1">
        <label class="text-sm font-medium text-n-slate-12"> Voice Speed </label>
        <input
          v-model.number="formData.voice_speed"
          type="number"
          min="0.5"
          max="2.0"
          step="0.1"
          class="px-3 py-2 text-sm border rounded-lg bg-n-solid-1 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div class="flex flex-col gap-1 flex-1">
        <label class="text-sm font-medium text-n-slate-12">
          Voice Temperature
        </label>
        <input
          v-model.number="formData.voice_temperature"
          type="number"
          min="0.0"
          max="1.0"
          step="0.1"
          class="px-3 py-2 text-sm border rounded-lg bg-n-solid-1 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
    </div>

    <TextArea
      v-model="formData.initial_message"
      label="Initial Message"
      placeholder="What the agent says when first connecting (optional)"
      rows="2"
    />

    <div class="flex justify-end gap-2 pt-4">
      <Button
        type="button"
        color="slate"
        variant="outline"
        :disabled="isSubmitting"
        @click="handleCancel"
      >
        {{ t('AI_VOICE.CANCEL') }}
      </Button>
      <Button
        type="submit"
        color="blue"
        :disabled="isSubmitting || !formData.name"
        :is-loading="isSubmitting"
      >
        {{
          mode === 'create'
            ? t('AI_VOICE.VOICE_AGENTS.CREATE')
            : t('AI_VOICE.VOICE_AGENTS.UPDATE')
        }}
      </Button>
    </div>
  </form>
</template>
